import { get, post } from './transport.js';
// ---------------------------------------------------------------------------
// Vocabulary (mirrors apps/api/src/domains/tasks/vocabulary.ts)
// ---------------------------------------------------------------------------
/**
 * WHO RAISED IT — `@cwos/shared`'s `TASK_ORIGINS`, RE-EXPORTED RATHER THAN
 * RETYPED.
 *
 * This line used to read
 *
 *     export type TaskOrigin = 'agent_generated' | 'human_created';
 *
 * and neither string appeared anywhere in `apps/api/src`. The server has always
 * stored `'phibian' | 'management'` — `task_origin_ck` in migration
 * 1758000000000 refuses anything else — so every surface keyed by this type was
 * being handed a word the server never sends. `apps/admin`'s `ORIGIN_LABEL`
 * rendered `undefined` for every real task, silently, for as long as the type
 * existed. Do not restate the members here; import them.
 */
export { RENEW_OWN_CREDENTIAL_TASK_TYPE, TASK_ORIGINS, TASK_PROSE_SUBJECTS } from '@cwos/shared';
/** Statuses in which the obligation is still outstanding. */
export const OPEN_TASK_STATUSES = Object.freeze([
    'open',
    'assigned',
    'escalated',
    'reopened',
]);
export function isOpenStatus(status) {
    return OPEN_TASK_STATUSES.includes(status);
}
/**
 * RESERVED AND NOT PERFORMED. Mirrors
 * `apps/api/src/domains/tasks/vocabulary.ts`'s
 * `RESERVED_UNPERFORMED_TASK_STATUSES`, and the reason is written out there in
 * full.
 *
 * IT IS EMPTY NOW. It held `'escalated'`, because nothing in the platform wrote
 * `escalationCount` or `lastEscalatedAt`, nothing recorded an `escalated`
 * event, and `task_escalation_policy` had one INSERT and no consumer — so the
 * status was one the server had never returned and could not return. The
 * escalation sweep (`tasks/service.runTaskEscalations`, on the
 * `system.task-escalations` queue) now produces it under the customer's own
 * policy, so every surface that derives its filter from
 * {@link REACHABLE_OPEN_TASK_STATUSES} offers it again — which is exactly what
 * the server-side docblock said would happen on the day escalation was built.
 *
 * The array stays, empty and typed, because it is where the next unperformed
 * status goes and because `REACHABLE_OPEN_TASK_STATUSES` is derived from it.
 *
 * WHAT IS STILL UNPERFORMED, and is not a status: reminders (`lastRemindedAt`
 * is written by nothing) and escalation ROUTING (`escalateTo` /
 * `notifyChannel` on a policy are read by nothing — the sweep changes the
 * obligation's state and does not decide who it reaches). See the server's
 * `RESERVED_UNPERFORMED_ESCALATION_ROUTING`.
 */
export const RESERVED_UNPERFORMED_TASK_STATUSES = Object.freeze([]);
const RESERVED_STATUS_SET = new Set(RESERVED_UNPERFORMED_TASK_STATUSES);
export function isReservedUnperformedStatus(status) {
    return RESERVED_STATUS_SET.has(status);
}
/**
 * The outstanding statuses a surface may offer or sweep for — {@link
 * OPEN_TASK_STATUSES} minus {@link RESERVED_UNPERFORMED_TASK_STATUSES},
 * derived so the two cannot drift. Asking the server for a reserved status is a
 * round trip that can only ever come back empty.
 */
export const REACHABLE_OPEN_TASK_STATUSES = Object.freeze(OPEN_TASK_STATUSES.filter((status) => !RESERVED_STATUS_SET.has(status)));
/**
 * The statuses in which NOTHING IS OWED. `verified` because Phibian watched the
 * condition resolve; `closed` because somebody closed it.
 *
 * `completion_claimed` is deliberately NOT here: a person said it was done and
 * nobody confirmed it, so it stays in the queue where both the worker and a
 * reviewer can still see it.
 */
export const SETTLED_TASK_STATUSES = Object.freeze(['verified', 'closed']);
/**
 * The actionable set — the default of `GET /api/tasks/mine`, and the
 * complement of {@link SETTLED_TASK_STATUSES}. Named as the complement so a
 * status this file has not heard of counts as work rather than vanishing from
 * the one surface a worker is measured against.
 */
export const WORKER_QUEUE_STATUSES = Object.freeze([
    'open',
    'assigned',
    'escalated',
    'reopened',
    'completion_claimed',
]);
/** The library's exact prose label for each routing target. */
export const ROUTING_TARGET_LABEL = Object.freeze({
    user: 'specific user',
    supervisor: 'supervisor',
    program_manager: 'program manager',
    superuser: 'superuser',
    role_queue: 'role/team queue',
});
/**
 * Decide what this task's completion means.
 *
 * IT DOES NOT READ THE STATUS WORD to answer "is this verified". `verifiedAt`
 * and `verificationActor` are set by the database at the moment an observation
 * proved the condition resolved, and they are the fact. `status` is a
 * projection of the lifecycle that a future state could widen; the
 * verification stamp cannot be produced any other way.
 *
 * The consequence that matters: a `completion_claimed` task returns
 * `phibianVerified: false` in EVERY mode, including hybrid, because a claim
 * awaiting observation is exactly the honest state hybrid exists to express.
 */
export function completionClaim(task) {
    const attested = {
        attestedByUserId: task.attestedBy,
        attestedAt: task.attestedAt,
        attestationStatement: task.attestationStatement,
    };
    // The verification stamp, and nothing else, makes a verification.
    if (task.verifiedAt !== null && task.verificationActor === 'phibian') {
        return { kind: 'verified', phibianVerified: true, ...attested, verifiedAt: task.verifiedAt };
    }
    if (task.attestedAt !== null) {
        return {
            // hybrid means Phibian is STILL WATCHING a claim it has not confirmed.
            kind: task.verificationMode === 'hybrid' ? 'watching' : 'claimed',
            phibianVerified: false,
            ...attested,
            verifiedAt: null,
        };
    }
    return {
        kind: 'none',
        phibianVerified: false,
        ...attested,
        verifiedAt: null,
    };
}
/** True when this task's mode permits Phibian to observe the outcome at all. */
export function isObservableMode(mode) {
    return mode === 'system_verified' || mode === 'hybrid';
}
/**
 * The sentence a surface may say about a mode, before anything has happened.
 * Short, and deliberately in the product's own voice rather than the schema's.
 */
export const VERIFICATION_MODE_LABEL = Object.freeze({
    system_verified: 'Phibian verifies',
    human_attested: 'Person attests',
    hybrid: 'Person attests, Phibian watches',
});
export const VERIFICATION_MODE_EXPLANATION = Object.freeze({
    system_verified: 'Phibian watches the underlying condition and completes this itself. No person can mark it done.',
    human_attested: 'Phibian cannot observe this outcome. A person marks it done, and it stands as that person’s claim.',
    hybrid: 'A person marks it done and Phibian keeps watching. It becomes verified only if Phibian sees the condition resolve.',
});
/**
 * One status or many, as the one comma-separated value the transport can carry.
 * `undefined` stays `undefined`, so "I did not filter" never becomes an empty
 * filter that matches nothing.
 */
function statusParam(status) {
    if (status === undefined)
        return undefined;
    return typeof status === 'string' ? status : [...status].join(',');
}
/**
 * `GET /api/tasks`
 *
 * Needs `task:read` AND `directory:read` — a task names the person responsible
 * for it, so reading one is reading the workforce. (The route borrowed
 * `alerts:read` before 1758090000000 seeded the real keys; it does not any
 * more, and `routes.ts:107` is the binding.) `task:read_all` widens the reach
 * from your own work and your reports' to the organization; it is not a second
 * read key. The 403 body names whichever key was refused.
 */
export function list(query = {}, options) {
    const { status, ...rest } = query;
    return get('/api/tasks', {
        ...options,
        query: { ...rest, status: statusParam(status) },
    });
}
/**
 * `GET /api/tasks/mine` — THE WORKER'S OWN ACTIONABLE QUEUE, resolved by the
 * server.
 *
 * One request where there were five, and it needs no user id: whose queue this
 * is comes from the session, and there is no field on {@link WorkerQueueQuery}
 * that could name anybody else. That absence is the contract. Filtering
 * `/api/tasks` by `responsibleUserId` cannot reproduce this answer — a
 * role-queue task has no responsible user, so it matches no value of that
 * filter — which is why a worker's role-queue work was invisible to their own
 * queue.
 *
 * Needs `task:read` and `directory:read`. It is strictly narrower than
 * `GET /api/tasks`: no direct reports, and `task:read_all` widens nothing here.
 *
 * `statuses` defaults to everything still owed — the four open statuses plus
 * `completion_claimed`. An empty page from this call is a MEASURED ZERO: every
 * caller may ask about their own work, so there is no refusal hiding in it.
 * `attestationReach` is where a refusal would appear.
 */
export function mine(query = {}, options) {
    const { status, ...rest } = query;
    return get('/api/tasks/mine', {
        ...options,
        query: { ...rest, status: statusParam(status) },
    });
}
/** `GET /api/tasks/:id` — task + immutable history + observations + attestations. */
export function detail(id, options) {
    return get(`/api/tasks/${id}`, options);
}
/** `POST /api/tasks` → 201 `{ task }`. */
export async function create(input, options) {
    const body = await post('/api/tasks', input, options);
    return body.task;
}
/** `POST /api/tasks/:id/assign` → `{ task }`. */
export async function assign(id, input, options) {
    const body = await post(`/api/tasks/${id}/assign`, input, options);
    return body.task;
}
/**
 * `POST /api/tasks/:id/complete` → `{ task }`.
 *
 * THIS IS AN ATTESTATION, NOT A VERIFICATION. It produces
 * `status: 'completion_claimed'` and an attestation row bearing the caller's
 * name. It never produces `verified`, in any mode. The function is named
 * `attestCompletion` rather than `complete` so a call site cannot read as
 * though pressing a button finished the matter.
 */
export async function attestCompletion(id, input, options) {
    const body = await post(`/api/tasks/${id}/complete`, input, options);
    return body.task;
}
/**
 * `POST /api/tasks/:id/observations` → 201 {@link ObservationOutcome}.
 *
 * The ONLY door to `verified`, and it is Phibian's door: the server attributes
 * the observation to the platform, never to the session that carried the call.
 * Read `outcome.verified` — do not re-derive it from `outcome.task.status`.
 */
export function recordObservation(id, input, options) {
    return post(`/api/tasks/${id}/observations`, input, options);
}
/** `POST /api/tasks/:id/reopen` → `{ task }`. A reason is required. */
export async function reopen(id, reason, options) {
    const body = await post(`/api/tasks/${id}/reopen`, { reason }, options);
    return body.task;
}
/** `POST /api/tasks/:id/close` → `{ task }`. */
export async function close(id, note, options) {
    const body = await post(`/api/tasks/${id}/close`, note ? { note } : {}, options);
    return body.task;
}
/**
 * `POST /api/tasks/escalation-policies` → 201 `{ policy }`.
 *
 * There is no default timing and there must not be one on this side either:
 * `overdueAfterMinutes` is required because the customer decides how long is
 * too long.
 */
export async function createEscalationPolicy(input, options) {
    const body = await post('/api/tasks/escalation-policies', input, options);
    return body.policy;
}
/**
 * `GET /api/tasks/escalation-policies` → `{ policies }` (needs `task:read`).
 *
 * THE READ THAT DID NOT EXIST. `createEscalationPolicy` shipped with no list
 * beside it, so a policy could be created and never seen again — and
 * `TaskView.escalationPolicyId` could never be populated by any UI, because
 * nothing could show a person the id to put there. A write-only resource is a
 * resource nothing can use.
 *
 * `includeInactive` because `active = false` is how a policy is retired (there
 * is no delete), and a settings screen that cannot see a retired policy cannot
 * un-retire one either. Omitted, the list is the policies in force.
 *
 * WHAT A POLICY DOES AND DOES NOT DO. `overdueAfterMinutes`,
 * `repeatAfterMinutes` and `maxEscalations` are ENFORCED — the escalation sweep
 * raises a task past its due boundary to `escalated` under exactly those
 * numbers. `escalateTo` and `notifyChannel` are STORED AND NOT ACTED ON: the
 * sweep changes the obligation's state and does not decide who the raised task
 * reaches or tell anybody. A screen offering those two fields is describing a
 * policy the platform half-applies and should say so.
 */
export async function listEscalationPolicies(query = {}, options) {
    const body = await get('/api/tasks/escalation-policies', {
        ...options,
        query: query.includeInactive ? { includeInactive: 'true' } : {},
    });
    return body.policies;
}
//# sourceMappingURL=tasks.js.map